import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { ru } from './locales/ru';
import type { Translation } from './locales/types';

/**
 * i18n from day one.
 *
 * WalletFront hardcoded Russian into JSX — and into the transport layer — so
 * adding English later would mean touching every file. Novix never puts a
 * user-visible string anywhere but these resource files. When a screen is ported
 * from WalletFront, its strings are extracted at porting time, not "later".
 */

/**
 * Every language a customer can choose. The same list as the server's
 * `UserLanguages.Supported`: a code missing on either side is a language that
 * either cannot be saved or renders as Russian.
 */
export const SUPPORTED_LANGUAGES = [
  'ru',
  'en',
  'uk',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'ro',
  'sl',
  'ar',
  'zh',
  'ja',
  'ko',
  'vi'
] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'ru';

export const LANGUAGE_STORAGE_KEY = 'novix.lang';

/**
 * Each language in its own words — a picker is read by someone who may not
 * read the language the app is currently in.
 */
export const LANGUAGE_NAMES: Record<Language, string> = {
  ru: 'Русский',
  en: 'English',
  uk: 'Українська',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
  it: 'Italiano',
  ro: 'Română',
  sl: 'Slovenščina',
  ar: 'العربية',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  vi: 'Tiếng Việt'
};

/**
 * BCP-47 locale for Intl. Arabic keeps Latin digits: amounts and addresses are
 * copied, compared and pasted, and a balance in Arabic-Indic digits next to a
 * TRON address in Latin ones is two numbering systems on one screen.
 */
const INTL_LOCALES: Record<Language, string> = {
  ru: 'ru-RU',
  en: 'en-US',
  uk: 'uk-UA',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  pt: 'pt-BR',
  it: 'it-IT',
  ro: 'ro-RO',
  sl: 'sl-SI',
  ar: 'ar-u-nu-latn',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  vi: 'vi-VN'
};

const RIGHT_TO_LEFT: ReadonlySet<Language> = new Set(['ar']);

/**
 * Russian is bundled, because it is the default and the fallback for any key a
 * translation lacks. Every other language is its own chunk, fetched when chosen:
 * fifteen full translations in the entry bundle would make every customer
 * download fourteen they will never read.
 */
const LOADERS: Record<Exclude<Language, 'ru'>, () => Promise<Translation>> = {
  en: () => import('./locales/en').then((module) => module.en),
  uk: () => import('./locales/uk').then((module) => module.uk),
  de: () => import('./locales/de').then((module) => module.de),
  fr: () => import('./locales/fr').then((module) => module.fr),
  es: () => import('./locales/es').then((module) => module.es),
  pt: () => import('./locales/pt').then((module) => module.pt),
  it: () => import('./locales/it').then((module) => module.it),
  ro: () => import('./locales/ro').then((module) => module.ro),
  sl: () => import('./locales/sl').then((module) => module.sl),
  ar: () => import('./locales/ar').then((module) => module.ar),
  zh: () => import('./locales/zh').then((module) => module.zh),
  ja: () => import('./locales/ja').then((module) => module.ja),
  ko: () => import('./locales/ko').then((module) => module.ko),
  vi: () => import('./locales/vi').then((module) => module.vi)
};

export const resources = {
  ru: { translation: ru }
} as const;

export function isLanguage(value: string | null | undefined): value is Language {
  return (
    value !== null &&
    value !== undefined &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * The language comes from the user's OWN choice or from the default. Never from
 * the browser.
 *
 * `i18next-browser-languagedetector` used to sit in front of this with
 * `order: ['localStorage', 'navigator']`. `navigator` meant that any visitor
 * whose browser reported `en-*` — which includes most default installs outside
 * Russia and every headless/CI browser — was served English on a
 * Russian-facing product, overriding the `ru` default that was written three
 * lines below it. The detector is gone: storage is read directly here, and if
 * it holds nothing the answer is `ru`. Once signed in, the profile's language
 * wins (see `useProfile`).
 */
function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_LANGUAGE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  // Other languages are added at runtime by `loadLanguage`.
  partialBundledLanguages: true,
  interpolation: {
    // React escapes for us; double-escaping mangles currency symbols.
    escapeValue: false
  },
  returnNull: false
});

async function loadLanguage(language: Language): Promise<void> {
  if (language === 'ru' || i18n.hasResourceBundle(language, 'translation')) return;
  i18n.addResourceBundle(language, 'translation', await LOADERS[language]());
}

function applyToDocument(language: Language): void {
  document.documentElement.lang = language;
  document.documentElement.dir = RIGHT_TO_LEFT.has(language) ? 'rtl' : 'ltr';
}

/**
 * Switches the interface. The translation is fetched first, so the screen goes
 * from one complete language to another rather than through a Russian flash.
 * A failed fetch (offline) leaves the current language in place.
 */
export async function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* storage unavailable — the choice still applies for this session */
  }

  if (i18n.language === language) return;

  try {
    await loadLanguage(language);
  } catch {
    return;
  }

  await i18n.changeLanguage(language);
  applyToDocument(language);
}

/** Resolves once the stored language is loaded and applied. Awaited before the first render. */
export const i18nReady: Promise<void> = setLanguage(readStoredLanguage()).then(() => {
  applyToDocument(isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE);
});

/** The active language, narrowed to the supported set. */
export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

/** BCP-47 locale for Intl, derived from the active language. */
export function currentLocale(): string {
  return INTL_LOCALES[currentLanguage()];
}

export default i18n;
